import { useMutation } from "@tanstack/react-query";
import useAuthenticatedApiRequest from "../../AuthenticatedApiRequest";
import TchrURL from "../../TchrURL";

interface FileUploadResponse {
  exists: boolean;
  filename: string;
  uploadPath: string;
  rawPath: string;
  validUntil: number;
}

const useFileUploadComplete = (template: boolean = false) => {
  return useMutation({
    mutationKey: ["file", "upload", "complete", template ? "template" : "regular"],
    mutationFn: async ({
      filename,
    }: {
      filename: string;
    }) => {
      const url = new TchrURL("files/complete");
      const searchParams = new URLSearchParams();
      searchParams.append("filename", filename);
      searchParams.append("template", template.toString());
      url.search = searchParams.toString();
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response;
    },
    retry: 3,
  });
}

const useFileUploadToPath = (template: boolean = false) => {
  return useMutation({
    mutationKey: ["file", "upload", "path", "put", template ? "template" : "regular"],
    mutationFn: async ({
      uploadPath,
      binary,
    }: {
      uploadPath: string;
      binary: ArrayBuffer;
    }) => {
      const url = new URL(uploadPath);
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
        },
        body: binary,
      });
      return response;
    }
  })
};

const useFileUpload = (template: boolean = false) => {
  const { get } = useAuthenticatedApiRequest();
  const { mutateAsync: uploadFile } = useFileUploadToPath(template);
  const { mutateAsync: completeFileUpload } = useFileUploadComplete(template);
  return useMutation<FileUploadResponse, Error, {
    filename: string;
    hash: string;
    binary: ArrayBuffer;
  }, {
    filename: string;
    hash: string;
    binary: ArrayBuffer;
  }>({
    mutationKey: ["file", "upload", "path", "get", template ? "template" : "regular"],
    mutationFn: async ({
      filename,
      hash,
      binary
    }: {
      filename: string;
      hash: string;
      binary: ArrayBuffer;
    }) => {
      const url = new TchrURL("files/upload");
      const searchParams = new URLSearchParams();
      searchParams.append("filename", filename);
      searchParams.append("hash", hash);
      searchParams.append("template", template.toString());
      url.search = searchParams.toString();

      const response = await get(url).then(async (res) => {
        const json = await res.json() as FileUploadResponse;
        if (json.exists) {
          await completeFileUpload({
            filename: json.filename,
          });
        } else {
          await uploadFile({
            uploadPath: json.uploadPath,
            binary,
          }).then(async (res2) => {
            if (res2.status !== 201) {
              throw new Error("Failed to upload file");
            }
            const completeResponse = await completeFileUpload({
              filename: json.filename,
            });

            return completeResponse;
          }).catch((err) => {
            console.error("Error uploading file", err);
            throw new Error("Failed to upload file");
          }
          );
        }

        return json;
      });
      return response;
    },
  });
};

export default useFileUpload;

// Template-specific hook for convenience
export const useTemplateFileUpload = () => useFileUpload(true);
